import React, { useMemo, useState } from "react";

const COMPANY_NAME = "StarEpoch Labs LLC";
const SUPPORT_EMAIL = "support@starepochlabs.com";

// 你要的一行地址（用于网页展示/表单）
const MAILING_ADDRESS_ONE_LINE = "30 N Gould St Ste N, Sheridan, WY 82801";

// 同时保留多行版本（用于更“正式”的展示）
const MAILING_ADDRESS_LINES = [
  "30 N Gould St Ste N",
  "Sheridan, WY 82801",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General inquiry",
    message: "",
  });

  const mailtoHref = useMemo(() => {
    const subject = encodeURIComponent(`[StarEpoch Labs] ${form.subject || "Contact"}`);
    const body = encodeURIComponent(
      `Name: ${form.name || ""}\nEmail: ${form.email || ""}\n\nMessage:\n${form.message || ""}\n\n---\nSent from starepochlabs.com contact form (client-side)`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, [form]);

  const onChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div className="space-y-10">
      {/* Header */}
      <section className="rounded-3xl border border-white/10 bg-white/5 p-10">
        <div className="space-y-3">
          <p className="text-sm font-medium tracking-wide text-slate-200/80">
            CONTACT
          </p>
          <h1 className="text-3xl font-semibold text-white md:text-4xl">
            Get in touch
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-slate-200/80">
            We&apos;re here to help with support requests, account questions, and
            security or compliance inquiries. For the fastest response, email us
            with a clear subject line.
          </p>
        </div>

        {/* Quick actions */}
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500"
          >
            Email Support
          </a>
          <a
            href="/trust"
            className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
          >
            Trust Center
          </a>
          <a
            href="/privacy"
            className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
          >
            Privacy Policy
          </a>
        </div>
      </section>

      {/* Grid */}
      <section className="grid gap-6 md:grid-cols-2">
        {/* Contact card */}
        <div className="rounded-3xl border border-white/10 bg-white/5 p-8">
          <h2 className="text-xl font-semibold text-white">Support</h2>

          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
              <div className="text-sm text-slate-200/70">Email</div>
              <a
                className="mt-1 inline-block text-base font-semibold text-red-400 hover:text-red-300"
                href={`mailto:${SUPPORT_EMAIL}`}
              >
                {SUPPORT_EMAIL}
              </a>
              <div className="mt-2 text-sm leading-relaxed text-slate-200/70">
                Response time: typically within 1–2 business days.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
              <div className="text-sm text-slate-200/70">Mailing address</div>
              <div className="mt-2 text-sm leading-relaxed text-slate-100">
                <div className="font-medium">{COMPANY_NAME}</div>
                <div className="mt-2 space-y-1 text-slate-200/80">
                  {MAILING_ADDRESS_LINES.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>

                {/* 一行格式（你要求的 web address） */}
                <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-slate-200/80">
                  <span className="text-slate-200/60">One-line:</span>{" "}
                  {MAILING_ADDRESS_ONE_LINE}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
              <div className="text-sm text-slate-200/70">Security reporting</div>
              <div className="mt-2 text-sm leading-relaxed text-slate-200/80">
                If you believe you&apos;ve found a security issue, please email{" "}
                <a className="font-semibold text-red-400 hover:text-red-300" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>{" "}
                with the subject <span className="font-semibold text-slate-100">“Security Report”</span>.
              </div>
              <div className="mt-2 text-xs text-slate-200/60">
                Please include reproduction steps, affected endpoints, and any relevant logs.
              </div>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div className="rounded-3xl border border-white/10 bg-white/5 p-8">
          <h2 className="text-xl font-semibold text-white">Send a message</h2>
          <p className="mt-2 text-sm text-slate-200/70">
            This form opens your email client (no server submission).
          </p>

          <div className="mt-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Name">
                <input
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </Field>

              <Field label="Email">
                <input
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </Field>
            </div>

            <Field label="Subject">
              <select
                value={form.subject}
                onChange={onChange("subject")}
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/40"
              >
                <option className="bg-[#070A12]">General inquiry</option>
                <option className="bg-[#070A12]">Support request</option>
                <option className="bg-[#070A12]">Account & billing</option>
                <option className="bg-[#070A12]">Security Report</option>
                <option className="bg-[#070A12]">Compliance</option>
              </select>
            </Field>

            <Field label="Message">
              <textarea
                value={form.message}
                onChange={onChange("message")}
                placeholder="Tell us how we can help..."
                rows={7}
                className="w-full resize-none rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
              />
            </Field>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a
                href={mailtoHref}
                className="inline-flex items-center justify-center rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500"
              >
                Open Email Client
              </a>

              <button
                type="button"
                onClick={() =>
                  setForm({ name: "", email: "", subject: "General inquiry", message: "" })
                }
                className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
              >
                Clear
              </button>

              <div className="text-xs text-slate-200/60">
                We do not request sensitive personal data via email.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance / Company */}
      <section className="rounded-3xl border border-white/10 bg-white/5 p-8">
        <h2 className="text-xl font-semibold text-white">Company</h2>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <InfoCard title="Legal name" value={COMPANY_NAME} />
          <InfoCard title="Support email" value={SUPPORT_EMAIL} />
          <InfoCard title="Mailing address" value={MAILING_ADDRESS_ONE_LINE} />
        </div>

        <div className="mt-6 text-sm leading-relaxed text-slate-200/70">
          For policy documents, please visit{" "}
          <a className="font-semibold text-red-400 hover:text-red-300" href="/privacy">
            Privacy
          </a>{" "}
          and{" "}
          <a className="font-semibold text-red-400 hover:text-red-300" href="/terms">
            Terms
          </a>
          . For trust-related details, see{" "}
          <a className="font-semibold text-red-400 hover:text-red-300" href="/trust">
            Trust Center
          </a>
          .
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-sm font-medium text-slate-200/80">{label}</div>
      {children}
    </label>
  );
}

function InfoCard({ title, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
      <div className="text-sm text-slate-200/70">{title}</div>
      <div className="mt-2 text-sm font-semibold text-slate-100">{value}</div>
    </div>
  );
}
